import { Grid } from "@mui/material";
import { Table, Tag } from "antd";
import { useMemo, useState } from "react";
import { showWarningSwal } from "../../functions/alert";
import { dismissIjhsCandidateGroup } from "../../functions/postData";
import NunitoText from "../general/NunitoText";
import ReusableButton from "../general/ReusableButton";

const cardStyle = {
  width: "100%",
  border: "1px solid #D0D0D0",
  borderRadius: 12,
  padding: "16px 20px 12px",
  marginBottom: 20,
  backgroundColor: "#FFF",
  boxShadow: "0px 1px 4px rgba(0,0,0,0.08)",
};

const memberKey = (member) =>
  String(member.participant_index || member.index_no || "");

const normalizeIdentifier = (value) => {
  if (value == null || value === "") return null;
  return String(value);
};

const displayValue = (value) => value || "—";

const schoolKey = (member) => {
  const school = member.school;
  if (!school) return null;
  if (school.id != null) return `id:${school.id}`;
  if (school.name) return `name:${school.name}`;
  return null;
};

const membersShareSameSchool = (members) => {
  if (members.length === 0) return false;
  const firstKey = schoolKey(members[0]);
  if (firstKey == null) return false;
  return members.every((member) => schoolKey(member) === firstKey);
};

const countByIdentifier = (members) => {
  const counts = new Map();
  members.forEach((member) => {
    const id = normalizeIdentifier(member.identifier);
    if (id == null) return;
    counts.set(id, (counts.get(id) || 0) + 1);
  });
  return counts;
};

const isAlreadyLinkedMember = (member, identifierCounts) => {
  const id = normalizeIdentifier(member.identifier);
  return id != null && (identifierCounts.get(id) || 0) > 1;
};

const selectionAlreadyFullyLinked = (selectedMembers) => {
  if (selectedMembers.length < 2) return false;
  const first = normalizeIdentifier(selectedMembers[0].identifier);
  if (first == null) return false;
  return selectedMembers.every(
    (member) => normalizeIdentifier(member.identifier) === first
  );
};

const countryName = (value, fallback) =>
  (typeof value === "string" && value) ||
  value?.name ||
  value?.display_name ||
  fallback;

const REASON_WITH_SCHOOL =
  "exact name + same country + same organization + same school · identifiers differ / missing";
const REASON_WITHOUT_SCHOOL =
  "exact name + same country + same organization · identifiers differ / missing";
const ALREADY_LINKED_HINT =
  "Selected records already share the same identifier. Include another unlinked record to tag them together.";
const SELECT_TWO_HINT =
  "Select two or more participant records from the list.";

export default function IjhsCandidateGroupCard({
  group,
  onTagSelected,
  onDismissed,
  setLoading,
}) {
  const members = group.members || [];
  const [selected, setSelected] = useState(() => members.map(memberKey));
  const reason = membersShareSameSchool(members)
    ? REASON_WITH_SCHOOL
    : REASON_WITHOUT_SCHOOL;

  const identifierCounts = useMemo(
    () => countByIdentifier(members),
    [members]
  );

  const selectedMembers = useMemo(
    () => members.filter((member) => selected.includes(memberKey(member))),
    [members, selected]
  );

  const onlyAlreadyLinked = selectionAlreadyFullyLinked(selectedMembers);
  const canTag = selected.length >= 2 && !onlyAlreadyLinked;

  const onTag = () => {
    if (selected.length < 2) {
      return showWarningSwal(
        "Select at least two members in this group to tag."
      );
    }
    if (onlyAlreadyLinked) {
      return showWarningSwal(ALREADY_LINKED_HINT);
    }
    onTagSelected(selectedMembers);
  };

  const onDismiss = () => {
    setLoading?.(true);
    dismissIjhsCandidateGroup(group.id)
      .then((data) => onDismissed(data))
      .catch((e) => console.log(e))
      .finally(() => setLoading?.(false));
  };

  const groupCountryName = countryName(group.country, "—");
  const count = group.members_count ?? members.length;

  const columns = useMemo(
    () => [
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        render: (name, member) => (
          <span>
            {name}
            {isAlreadyLinkedMember(member, identifierCounts) && (
              <Tag style={{ marginInlineStart: 8 }}>already linked</Tag>
            )}
          </span>
        ),
      },
      {
        title: "Country",
        key: "country",
        render: (_, member) => countryName(member.country, groupCountryName),
      },
      {
        title: "Index",
        key: "index",
        render: (_, member) => memberKey(member) || "—",
      },
      {
        title: "Competition",
        key: "competition",
        render: (_, member) =>
          member.competition?.alias || member.competition?.name || "—",
      },
      {
        title: "School",
        key: "school",
        render: (_, member) => member.school?.name || "—",
      },
      {
        title: "Identifier",
        dataIndex: "identifier",
        key: "identifier",
        render: (value) => displayValue(value),
      },
      {
        title: "Award",
        dataIndex: "award",
        key: "award",
        render: (value) => displayValue(value),
      },
    ],
    [groupCountryName, identifierCounts]
  );

  return (
    <div style={cardStyle}>
      <NunitoText
        value={`${group.name_key} · ${count} records`}
        fontSize={18}
        fontWeight={700}
        color="#144A94"
        align="left"
      />
      <NunitoText
        value={`Reason: ${reason}`}
        fontSize={14}
        fontWeight={400}
        color="#555"
        marginTop={4}
        align="left"
      />
      {onlyAlreadyLinked && (
        <NunitoText
          value={ALREADY_LINKED_HINT}
          fontSize={13}
          fontWeight={400}
          color="#A15C00"
          marginTop={6}
          align="left"
        />
      )}
      <Table
        size="small"
        pagination={false}
        showSorterTooltip={false}
        style={{ marginTop: 12 }}
        rowKey={memberKey}
        columns={columns}
        dataSource={members}
        rowSelection={{
          selectedRowKeys: selected,
          onChange: setSelected,
        }}
      />
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        style={{ marginTop: 12 }}
      >
        <ReusableButton
          text="Not the same"
          fontSize={14}
          bgColor="#E83042"
          height={40}
          onClick={onDismiss}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {selected.length < 2 && (
            <NunitoText
              value={SELECT_TWO_HINT}
              fontSize={13}
              fontWeight={600}
              color="#A15C00"
              align="right"
              width="auto"
            />
          )}
          <ReusableButton
            text="Tag selected as same"
            fontSize={14}
            bgColor="#5E75C3"
            height={40}
            onClick={onTag}
            disabledButton={!canTag}
          />
        </div>
      </Grid>
    </div>
  );
}
